import LanguageIcon from '@mui/icons-material/Language';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useLanguage } from '../../hooks/useLanguage';
import { theme } from '../../theme';

const LanguageButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${theme.colors.textMuted} !important;
    border: 1px solid transparent !important;
    border-radius: 4px !important;
    padding: 8px !important;
    transition: all 0.3s ease !important;

    &:hover {
      color: ${theme.colors.primary} !important;
      background: ${theme.colors.backgroundLight} !important;
      border-color: ${theme.colors.border} !important;
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    color: ${theme.colors.text} !important;
    background: ${theme.colors.backgroundSemi} !important;
    font-family: ${theme.typography.fonts.mono} !important;
    font-size: 14px !important;
    padding: 12px 16px !important;

    &:hover {
      background: ${theme.colors.backgroundLight} !important;
      color: ${theme.colors.primary} !important;
    }

    &.Mui-selected {
      background: ${theme.colors.backgroundLight} !important;
      color: ${theme.colors.primary} !important;

      &::before {
        content: '> ';
        color: ${theme.colors.primary};
        margin-right: 8px;
      }
    }
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    background: ${theme.colors.backgroundSemi} !important;
    border: 1px solid ${theme.colors.border} !important;
    border-radius: 4px !important;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.6) !important;
  }
`;

const LanguageFlag = styled.span`
  margin-right: 8px;
  font-size: 16px;
`;

const LanguageToggle: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage, supportedLanguages, changeLanguage, isReady } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode: string) => {
    const success = await changeLanguage(languageCode);
    if (success) {
      handleClose();
    } else {
      // Handle error - maybe show a toast notification
      console.error('Failed to change language to:', languageCode);
      handleClose();
    }
  };

  // Don't render until i18n is ready
  if (!isReady) {
    return null;
  }

  return (
    <>
      <Tooltip title="Change Language / Endre språk">
        <LanguageButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <LanguageIcon />
        </LanguageButton>
      </Tooltip>

      <StyledMenu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {supportedLanguages.map(language => (
          <StyledMenuItem
            key={language.code}
            selected={language.code === currentLanguage.code}
            onClick={() => handleLanguageChange(language.code)}
          >
            <LanguageFlag>{language.flag}</LanguageFlag>
            {language.name}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
};

export default LanguageToggle;
